<template>
  <div v-if="visible" class="kep-modal-overlay" @click.self="closeModal">
    <div class="kep-modal-card card">
      <div class="kep-modal-header">
        <h3 class="card-title">
          {{ signingMode === 'single' ? 'Підписання документа КЕП' : 'Пакетне підписання КЕП' }}
        </h3>
        <button
          type="button"
          class="close-btn"
          :disabled="loading"
          @click="closeModal"
        >
          ✕
        </button>
      </div>

      <div class="kep-modal-body stack-sm">
        <p class="field-hint">
          <template v-if="signingMode === 'single' && targetDocToSign">
            Документ: <strong>{{ targetDocToSign.name_ua || targetDocToSign.title || targetDocToSign.name }}</strong>
          </template>
          <template v-else>
            Обрано документів для підпису: <strong>{{ selectedDocCount }}</strong>
          </template>
        </p>

        <label class="field">
          <span class="field-label">АЦСК (Агентство / Банк) <span class="req">*</span></span>
          <select
            v-model="selectedCa"
            class="input"
            :disabled="loading || loadingCa"
          >
            <option value="" disabled>Оберіть надавача електронних довірчих послуг</option>
            <option v-for="ca in caList" :key="ca.id || ca.code" :value="ca.code || ca.id">
              {{ ca.name || ca.title }}
            </option>
          </select>
          <span v-if="loadingCa" class="field-hint">Завантаження списку АЦСК...</span>
        </label>

        <!-- Выбор файла ключа -->
        <label class="field">
          <span class="field-label">Файл особистого ключа (.dat, .pfx, .pk8, .zs2) <span class="req">*</span></span>
          <input
            type="file"
            class="input-file"
            accept=".dat,.pfx,.pk8,.zs2,.jks"
            :disabled="loading"
            @change="handleKepFileSelect"
          />
          <span v-if="kepFile" class="file-selected-info">
            📄 {{ kepFile.name }}
          </span>
        </label>

        <!-- Ввод пароля -->
        <label class="field">
          <span class="field-label">Пароль до ключа <span class="req">*</span></span>
          <input
            v-model="kepPassword"
            type="password"
            class="input"
            placeholder="Введіть пароль"
            :disabled="loading"
            @keyup.enter="onSubmit"
          />
        </label>
      </div>

      <div class="kep-modal-footer">
        <button
          type="button"
          class="btn btn-ghost"
          :disabled="loading"
          @click="closeModal"
        >
          Скасувати
        </button>
        <button
          type="button"
          class="btn btn-primary"
          :disabled="!isValid || loading"
          @click="onSubmit"
        >
          <span v-if="loading" class="el-icon-loading" style="margin-right: 6px;"></span>
          {{ loading ? 'Підписання...' : 'Підписати КЕП' }}
        </button>
      </div>
    </div>
  </div>
</template>

<script>

import {
  getCARegistryList
} from '@/api/onboarding';

export default {
  name: 'SignModal',
  props: {
    visible: {
      type: Boolean,
      default: false,
    },
    signingMode: {
      type: String,
      default: 'single', // 'single' | 'batch'
    },
    targetDocToSign: {
      type: Object,
      default: () => null,
    },
    selectedDocCount: {
      type: Number,
      default: 0,
    },
    loading: {
      type: Boolean,
      default: false,
    },
  },
  data() {
    return {
      kepFile: null,
      kepPassword: '',
      selectedCa: '',
      caList: [],
      loadingCa: false,
    };
  },
  computed: {
    isValid() {
      return !!this.selectedCa && !!this.kepFile && this.kepPassword.trim().length > 0;
    },
  },
  watch: {
    visible(val) {
      if (val) {
        this.resetForm();
        if (this.caList.length === 0) {
          this.fetchCaRegistries();
        }
      }
    },
  },
  methods: {
    resetForm() {
      this.kepFile = null;
      this.kepPassword = '';
      this.selectedCa = '';
    },

    async fetchCaRegistries() {
      this.loadingCa = true;
      try {
        const response = await getCARegistryList();
        const resData = response.data.data || response.data;

        this.caList = Array.isArray(resData) ? resData : (resData.result || resData.items || []);
      } catch (error) {
        console.error('Помилка завантаження АЦСК:', error);

        this.caList = [
          { code: 'PrivatBank', name: 'ПриватБанк' },
          { code: 'Mono', name: 'Монобанк' },
          { code: 'Oschad', name: 'Ощадбанк' },
          { code: 'Tax', name: 'ДПС України' },
          { code: 'Diia', name: 'Дія.Підпис' }
        ];
      } finally {
        this.loadingCa = false;
      }
    },

    closeModal() {
      if (this.loading) return;
      this.resetForm();
      this.$emit('update:visible', false);
      this.$emit('close');
    },

    handleKepFileSelect(event) {
      const files = event.target.files;
      if (files && files.length > 0) {
        this.kepFile = files[0];
      }
    },

    onSubmit() {
      if (!this.isValid || this.loading) return;

      this.$emit('submit', {
        caCode: this.selectedCa,
        file: this.kepFile,
        password: this.kepPassword,
      });
    },
  },
};
</script>

<style scoped>
.kep-modal-overlay {
  position: fixed;
  top: 0;
  left: 0;
  width: 100vw;
  height: 100vh;
  background: rgba(15, 23, 42, 0.55);
  backdrop-filter: blur(4px);
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 9999;
  padding: 16px;
}

.kep-modal-card {
  background: #ffffff;
  border-radius: 12px;
  max-width: 500px;
  width: 100%;
  padding: 24px;
  box-shadow: 0 20px 25px -5px rgba(0, 0, 0, 0.1), 0 10px 10px -5px rgba(0, 0, 0, 0.04);
}

.kep-modal-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 16px;
}

.close-btn {
  background: none;
  border: none;
  font-size: 18px;
  cursor: pointer;
  color: #64748b;
}

.close-btn:disabled {
  cursor: not-allowed;
  opacity: 0.5;
}

.input-file {
  display: block;
  width: 100%;
  padding: 8px;
  border: 1px solid #cbd5e1;
  border-radius: 6px;
  margin-top: 6px;
}

.file-selected-info {
  display: block;
  margin-top: 6px;
  font-size: 13px;
  color: #16a34a;
  font-weight: 600;
}

.kep-modal-footer {
  display: flex;
  justify-content: flex-end;
  gap: 12px;
  margin-top: 24px;
}
</style>
